'use client';
import { useEffect, useState } from 'react';
import Appbar from './Appbar';
import Image from 'next/image';
import { EventAction, EventType, QREventActions } from '../../config/GAConstants';
import { dynamicBusinessRoute } from '../../config/routes';
import AccountIcon from '../../resources/assets/accountIcon.svg';
import Arrow from '../../resources/assets/arrowRight.svg';
import SignoutIcon from '../../resources/assets/signout.svg';
import { navStyle, styles } from './topNav.styles';
import { BoxComponent, Sidebar, TypographyComponent } from '@zeus/web-components';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { getCookie } from 'cookies-next';
import { restaurantMenuList } from '@zeus/shared';
import { Box, List, ListItemButton, ListItemText } from '@mui/material';
import { sendGAEvent } from '@next/third-parties/google';
import { getUserDetailsById } from '../../services/personalInfo/PersonalInfo';
import { sendEventToPostHog, setUserAttribute, setUserAttributeForQR } from '../../utils/UtilFunctions';
import { useSelector } from 'react-redux';
import useAuthUser from '../hooks/auth/AuthUser';
import { useSignout } from '../hooks/auth/useSignout';
import { RootState } from '../store';

interface MenuItem {
  icon?: string;
  label: string;
  link: string;
}

// Component for the top navigation bar
const Topnav = ({
  rightPlacement,
  title,
  handleBack,
}: {
  rightPlacement?: boolean;
  title?: string;
  handleBack?: () => void;
}) => {
  // State to manage the sidebar open/close state
  const [open, setOpen] = useState<boolean>(false);
  const [fullName, setFullName] = useState<string>(' ');
  const [token, setToken] = useState<string | null>(null);
  const access = getCookie('AccessToken') || null;
  //store states
  const isQr = useSelector((state: RootState) => state.restaurant.isQr);
  const authUser = useAuthUser() || {};
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const token = getCookie('AccessToken') || null;
    setToken(token || access);
  }, [access]);

  const handleListItemClick = (row: MenuItem, index: number) => {
    const user = setUserAttribute();
    if (row.label == 'Home') {
      const destination = dynamicBusinessRoute();
      router.push(destination);
    } else if (row.label == 'My Orders') {
      router.push(row.link);
      sendGAEvent(EventType.EVENT, EventAction.OL_MY_ORDERS_TAPPED, user);
      sendEventToPostHog(EventType.EVENT, EventAction.OL_MY_ORDERS_TAPPED, user);
    } else {
      router.push(row.link);
    }
  };

  const getUserData = async () => {
    try {
      const userData = await getUserDetailsById();
      const { given_name, family_name } = userData?.data?.UserAttributes || {};

      if (given_name || family_name) {
        setFullName(`${given_name ?? ''}`.trim());
      }
    } catch (err) {
      console.log('err', err);
    }
  };
  useEffect(() => {
    if (token) {
      getUserData();
    }
  }, [token, authUser]);

  const handleSignoutClick = useSignout();
  const handleMyaccount = () => {
    const user = isQr ? setUserAttributeForQR() : setUserAttribute();
    const eventAction = isQr ? QREventActions.QR_MYACCOUNT_TAPPED : EventAction.OL_MYACCOUNT_TAPPED;
    sendEventToPostHog(EventType.EVENT, eventAction, user);
    sendGAEvent(EventType.EVENT, eventAction, user);
    if (pathname === '/myaccount') {
      setOpen(false);
    }
  };

  return (
    <>
      {/* Top app bar */}
      <Appbar setOpen={setOpen} title={title} handleBack={handleBack} />

      {/* Sidebar */}
      <Sidebar open={open} setOpen={setOpen} rightPlacement={rightPlacement}>
        {/* View cart modal */}

        {/* Top section of the sidebar */}
        <Link
          id="topnav-myaccount__link"
          href="/myaccount"
          style={styles.linkStyle}
          onClick={() => {
            handleMyaccount();
          }}
        >
          <BoxComponent sx={styles.topContainer}>
            <BoxComponent sx={styles.subContainerRight}>
              {/* Account icon */}
              <Image src={AccountIcon} alt="icon" className="icon" />
              <BoxComponent sx={{ marginLeft: '15px' }}>
                <TypographyComponent id="topnav-fullname__subtitle" style={styles.subtitle1}>
                  {fullName}
                </TypographyComponent>
                <TypographyComponent id="topnav-myaccount__subtitle" style={styles.subtitle2}>
                  My Account
                </TypographyComponent>
              </BoxComponent>
            </BoxComponent>
            {/* Right arrow icon */}
            <BoxComponent className="icon-container">
              <Image id="topnav-toggle__icon" src={Arrow} alt="icon" className="icon" />
            </BoxComponent>
          </BoxComponent>
        </Link>

        <BoxComponent sx={styles.menuContainer}>
          <Box sx={{ width: '100%', maxWidth: 360 }}>
            <List component="nav" sx={navStyle.listContainer}>
              {restaurantMenuList?.map((row, rowIndex) => {
                return (
                  <ListItemButton
                    id={`topnav-list-item_${row.label}`}
                    key={rowIndex}
                    selected={pathname.split('/')[1] === row.link.split('/')[1]}
                    onClick={() => handleListItemClick(row, rowIndex)}
                    sx={navStyle.listButton}
                  >
                    <ListItemText primary={`${row.label}`} sx={navStyle.listButtonText} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </BoxComponent>

        {/* Bottom section of the sidebar */}
        <BoxComponent id="topnav-signout__button" sx={styles.bottomContainer} onClick={handleSignoutClick}>
          {/* Signout icon */}
          <Image src={SignoutIcon} alt="signout" />

          {/* Signout text */}
          <TypographyComponent style={styles.subtitle3}>Sign Out</TypographyComponent>
        </BoxComponent>
      </Sidebar>
    </>
  );
};

export default Topnav;
